import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Tag, Search, Trash2, Edit3, Check, X, Loader2, Upload,
    Merge, AlertTriangle, Users, Building2, Briefcase, RefreshCw,
    Palette, BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAG_COLORS = [
    { name: 'Stone', value: '#78716c' },
    { name: 'Red', value: '#dc2626' },
    { name: 'Orange', value: '#ea580c' },
    { name: 'Amber', value: '#d97706' },
    { name: 'Green', value: '#16a34a' },
    { name: 'Emerald', value: '#059669' },
    { name: 'Teal', value: '#0d9488' },
    { name: 'Blue', value: '#2563eb' },
    { name: 'Indigo', value: '#4f46e5' },
    { name: 'Purple', value: '#9333ea' },
    { name: 'Pink', value: '#db2777' },
];

export default function TagManagementTab() {
    const { getAuthHeader } = useAuth();
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimerRef = useRef(null);
    const [selectedTags, setSelectedTags] = useState(new Set());

    // Rename state
    const [renamingTag, setRenamingTag] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [renaming, setRenaming] = useState(false);

    // Delete state
    const [deleteTag, setDeleteTag] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Merge state
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [mergeTarget, setMergeTarget] = useState('');
    const [merging, setMerging] = useState(false);

    // Import state
    const [showImportModal, setShowImportModal] = useState(false);
    const [csvText, setCsvText] = useState('');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    // Definitions (colors/categories)
    const [definitions, setDefinitions] = useState({});
    const [editingColorTag, setEditingColorTag] = useState(null);

    // v2.16.39 BUG-049/BUG-010 — Create tag state. Tags are normally
    // surfaced via an aggregation of entity.tags, so a standalone tag
    // with zero contacts won't appear in the grid until it's applied.
    // Creating here writes a tag_definition (the row the grid uses for
    // color/category), with a marker that lets it appear in the table
    // even when nothing holds it yet.
    const [showCreateTag, setShowCreateTag] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0].value);
    const [newTagCategory, setNewTagCategory] = useState('');
    const [creatingTag, setCreatingTag] = useState(false);

    // Analytics
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('table'); // 'table' | 'analytics'

    const loadDefinitions = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/tags/definitions`, { headers: getAuthHeader() });
            const map = {};
            (res.data || []).forEach(d => { map[d.tag.toLowerCase()] = d; });
            setDefinitions(map);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const loadAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        try {
            const res = await axios.get(`${API}/tags/analytics`, { headers: getAuthHeader() });
            setAnalytics(res.data);
        } catch { toast.error('Failed to load analytics'); }
        finally { setAnalyticsLoading(false); }
    }, [getAuthHeader]);

    const saveColor = async (tagName, color) => {
        try {
            await axios.post(`${API}/tags/definitions`, { tag: tagName, color }, { headers: getAuthHeader() });
            setDefinitions(prev => ({ ...prev, [tagName.toLowerCase()]: { ...prev[tagName.toLowerCase()], tag: tagName, color } }));
            setEditingColorTag(null);
            toast.success('Color saved');
        } catch { toast.error('Failed to save color'); }
    };

    // v2.16.39 BUG-049/BUG-010 — create a new tag (standalone, zero contacts).
    const handleCreateTag = async () => {
        const name = (newTagName || '').trim();
        if (!name) { toast.error('Tag name is required'); return; }
        setCreatingTag(true);
        try {
            const payload = { tag: name, color: newTagColor };
            if (newTagCategory) payload.category = newTagCategory;
            await axios.post(`${API}/tags/definitions`, payload, { headers: getAuthHeader() });
            toast.success(`Created "${name}"`);
            setShowCreateTag(false);
            setNewTagName(''); setNewTagColor(TAG_COLORS[0].value); setNewTagCategory('');
            await loadDefinitions();
            // Refresh table so the new tag appears (count = 0 until applied).
            if (typeof loadTags === 'function') loadTags();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create tag');
        } finally {
            setCreatingTag(false);
        }
    };

    // Debounce search input — wait 300ms after typing stops before firing API call
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [search]);

    const loadTags = useCallback(async () => {
        setLoading(true);
        try {
            const params = debouncedSearch ? `?search=${encodeURIComponent(debouncedSearch)}` : '';
            const res = await axios.get(`${API}/admin/tags${params}`, { headers: getAuthHeader() });
            setTags(res.data || []);
        } catch {
            toast.error('Failed to load tags');
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, getAuthHeader]);

    useEffect(() => { loadTags(); loadDefinitions(); }, [loadTags, loadDefinitions]);

    // ── Rename ──
    const startRename = (tag) => {
        setRenamingTag(tag.tag);
        setRenameValue(tag.tag);
    };

    const handleRename = async () => {
        if (!renameValue.trim() || renameValue.trim() === renamingTag) {
            setRenamingTag(null);
            return;
        }
        setRenaming(true);
        try {
            const res = await axios.put(`${API}/admin/tags/rename`, {
                old_tag: renamingTag,
                new_tag: renameValue.trim(),
            }, { headers: getAuthHeader() });
            toast.success(res.data.message);
            setRenamingTag(null);
            loadTags();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Rename failed');
        } finally {
            setRenaming(false);
        }
    };

    // ── Delete ──
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await axios.delete(`${API}/admin/tags/${encodeURIComponent(deleteTag)}`, { headers: getAuthHeader() });
            toast.success(res.data.message);
            setDeleteTag(null);
            loadTags();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Delete failed');
        } finally {
            setDeleting(false);
        }
    };

    // ── Merge ──
    const handleMerge = async () => {
        if (!mergeTarget.trim()) {
            toast.error('Enter a target tag name');
            return;
        }
        setMerging(true);
        try {
            const res = await axios.put(`${API}/admin/tags/merge`, {
                source_tags: Array.from(selectedTags),
                target_tag: mergeTarget.trim(),
            }, { headers: getAuthHeader() });
            toast.success(res.data.message);
            setShowMergeModal(false);
            setSelectedTags(new Set());
            setMergeTarget('');
            loadTags();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Merge failed');
        } finally {
            setMerging(false);
        }
    };

    // ── Import ──
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setCsvText(ev.target.result);
            setImportResult(null);
        };
        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!csvText.trim()) {
            toast.error('Upload a CSV file first');
            return;
        }
        setImporting(true);
        try {
            const res = await axios.post(`${API}/admin/tags/import`, {
                csv_data: csvText,
            }, { headers: getAuthHeader() });
            setImportResult(res.data);
            toast.success(res.data.message);
            loadTags();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    // ── Selection ──
    const toggleSelect = (tag) => {
        setSelectedTags(prev => {
            const next = new Set(prev);
            if (next.has(tag)) next.delete(tag);
            else next.add(tag);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedTags.size === tags.length) {
            setSelectedTags(new Set());
        } else {
            setSelectedTags(new Set(tags.map(t => t.tag)));
        }
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Tag Management</h2>
                    <p className="text-xs text-stone-500 mt-0.5">{tags.length} tags across contacts, companies, and deals</p>
                </div>
                <div className="flex items-center gap-2">
                    {selectedTags.size >= 2 && (
                        <Button size="sm" variant="outline" onClick={() => { setMergeTarget(''); setShowMergeModal(true); }}>
                            <Merge className="w-3.5 h-3.5 mr-1.5" /> Merge ({selectedTags.size})
                        </Button>
                    )}
                    <div className="flex items-center bg-stone-100 rounded-lg p-0.5">
                        <button onClick={() => setActiveSection('table')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${activeSection === 'table' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}>
                            <Tag className="w-3 h-3 inline mr-1" />Tags
                        </button>
                        <button onClick={() => { setActiveSection('analytics'); if (!analytics) loadAnalytics(); }}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${activeSection === 'analytics' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}>
                            <BarChart3 className="w-3 h-3 inline mr-1" />Analytics
                        </button>
                    </div>
                    {/* v2.16.39 BUG-049 */}
                    <Button size="sm" onClick={() => { setNewTagName(''); setNewTagColor(TAG_COLORS[0].value); setNewTagCategory(''); setShowCreateTag(true); }}>
                        <Tag className="w-3.5 h-3.5 mr-1.5" /> New Tag
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setCsvText(''); setImportResult(null); setShowImportModal(true); }}>
                        <Upload className="w-3.5 h-3.5 mr-1.5" /> Import Tags
                    </Button>
                    <Button size="sm" variant="outline" onClick={loadTags}>
                        <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </div>

            {activeSection === 'table' && (
            <>
            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search tags..."
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                />
            </div>

            {/* Tag table */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                </div>
            ) : tags.length === 0 ? (
                <div className="text-center py-12">
                    <Tag className="w-10 h-10 text-stone-200 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">{search ? 'No tags match your search' : 'No tags found'}</p>
                </div>
            ) : (
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    {/* Table header */}
                    <div className="flex items-center px-4 py-2.5 bg-stone-50 border-b border-stone-200 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                        <div className="w-8">
                            <input type="checkbox" checked={selectedTags.size === tags.length && tags.length > 0}
                                onChange={toggleSelectAll}
                                className="w-4 h-4 rounded border-stone-300" />
                        </div>
                        <div className="flex-1">Tag</div>
                        <div className="w-20 text-center flex items-center gap-1 justify-center"><Users className="w-3 h-3" /> Contacts</div>
                        <div className="w-20 text-center flex items-center gap-1 justify-center"><Building2 className="w-3 h-3" /> Companies</div>
                        <div className="w-20 text-center flex items-center gap-1 justify-center"><Briefcase className="w-3 h-3" /> Deals</div>
                        <div className="w-16 text-center">Total</div>
                        <div className="w-24"></div>
                    </div>

                    {/* Tag rows */}
                    {tags.map(tag => (
                        <div key={tag.tag} className={`flex items-center px-4 py-2.5 border-b border-stone-100 hover:bg-stone-50 transition-colors group ${
                            selectedTags.has(tag.tag) ? 'bg-stone-50' : ''
                        }`}>
                            <div className="w-8">
                                <input type="checkbox" checked={selectedTags.has(tag.tag)}
                                    onChange={() => toggleSelect(tag.tag)}
                                    className="w-4 h-4 rounded border-stone-300" />
                            </div>
                            <div className="flex-1 min-w-0">
                                {renamingTag === tag.tag ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            value={renameValue}
                                            onChange={e => setRenameValue(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenamingTag(null); }}
                                            autoFocus
                                            className="px-2 py-1 text-sm border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                        />
                                        <button onClick={handleRename} disabled={renaming} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded">
                                            {renaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                        </button>
                                        <button onClick={() => setRenamingTag(null)} className="p-1 text-stone-400 hover:bg-stone-100 rounded">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-sm font-medium bg-stone-100 text-stone-700"
                                            style={definitions[tag.tag.toLowerCase()]?.color ? { borderLeft: `3px solid ${definitions[tag.tag.toLowerCase()].color}` } : {}}>
                                            <Tag className="w-3 h-3 text-stone-400" />
                                            {tag.tag}
                                        </span>
                                        {/* Color picker */}
                                        <div className="relative">
                                            <button
                                                onClick={() => setEditingColorTag(editingColorTag === tag.tag ? null : tag.tag)}
                                                className="w-5 h-5 rounded-full border border-stone-200 hover:border-stone-400 transition-colors shrink-0 opacity-0 group-hover:opacity-100"
                                                style={{ backgroundColor: definitions[tag.tag.toLowerCase()]?.color || '#d6d3d1' }}
                                                title="Set color"
                                            />
                                            {editingColorTag === tag.tag && (
                                                <div className="absolute left-0 top-7 z-50 bg-white border border-stone-200 rounded-lg shadow-lg p-2 flex flex-wrap gap-1.5 w-[180px]">
                                                    {TAG_COLORS.map(c => (
                                                        <button key={c.value} onClick={() => saveColor(tag.tag, c.value)}
                                                            className="w-6 h-6 rounded-full border-2 border-white hover:scale-110 transition-transform"
                                                            style={{ backgroundColor: c.value }}
                                                            title={c.name} />
                                                    ))}
                                                    <button onClick={() => saveColor(tag.tag, null)}
                                                        className="w-6 h-6 rounded-full border border-stone-300 hover:scale-110 transition-transform flex items-center justify-center text-stone-400 text-xs"
                                                        title="Remove color">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {definitions[tag.tag.toLowerCase()]?.category && (
                                            <span className="text-[10px] text-stone-400">{definitions[tag.tag.toLowerCase()].category}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="w-20 text-center text-sm text-stone-600">{tag.contacts || 0}</div>
                            <div className="w-20 text-center text-sm text-stone-600">{tag.accounts || 0}</div>
                            <div className="w-20 text-center text-sm text-stone-600">{tag.deals || 0}</div>
                            <div className="w-16 text-center text-sm font-semibold text-stone-900">{tag.total || 0}</div>
                            <div className="w-24 flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => startRename(tag)} title="Rename"
                                    className="p-1.5 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700">
                                    <Edit3 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => setDeleteTag(tag.tag)} title="Delete"
                                    className="p-1.5 rounded hover:bg-red-100 text-stone-400 hover:text-red-600">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            </>
            )}

            {/* Analytics Section */}
            {activeSection === 'analytics' && (
                <div className="space-y-6">
                    {analyticsLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                        </div>
                    ) : analytics ? (
                        <>
                            {/* Tag Frequency Distribution */}
                            {analytics.frequency?.length > 0 && (
                                <div className="bg-white border border-stone-200 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Tag Frequency (Top 20)
                                    </h3>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={analytics.frequency} layout="vertical" margin={{ left: 100 }}>
                                            <XAxis type="number" tick={{ fontSize: 11 }} />
                                            <YAxis dataKey="tag" type="category" tick={{ fontSize: 11 }} width={90} />
                                            <Tooltip formatter={(val) => [val, 'Contacts']} />
                                            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                                {analytics.frequency.map((entry, i) => {
                                                    const defn = definitions[entry.tag?.toLowerCase()];
                                                    return <Cell key={i} fill={defn?.color || '#78716c'} />;
                                                })}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Tag Growth Over Time */}
                            {analytics.growth?.length > 0 && (
                                <div className="bg-white border border-stone-200 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Tags Added Per Month
                                    </h3>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <BarChart data={analytics.growth}>
                                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} />
                                            <Tooltip formatter={(val) => [val, 'Tags added']} />
                                            <Bar dataKey="tags_added" fill="#78716c" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Conversion by Tag */}
                            {analytics.conversion?.length > 0 && (
                                <div className="bg-white border border-stone-200 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Conversion Rate by Tag
                                    </h3>
                                    <div className="space-y-2">
                                        {analytics.conversion.map(c => (
                                            <div key={c.tag} className="flex items-center gap-3">
                                                <span className="text-xs text-stone-700 w-32 truncate font-medium"
                                                    style={definitions[c.tag?.toLowerCase()]?.color ? { borderLeft: `3px solid ${definitions[c.tag.toLowerCase()].color}`, paddingLeft: 8 } : {}}>
                                                    {c.tag}
                                                </span>
                                                <div className="flex-1 bg-stone-100 rounded-full h-4 overflow-hidden">
                                                    <div className="h-full rounded-full bg-emerald-500 transition-all"
                                                        style={{ width: `${Math.min(c.rate, 100)}%` }} />
                                                </div>
                                                <span className="text-xs text-stone-600 w-20 text-right">
                                                    {c.rate}% ({c.converted}/{c.total})
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tag Co-occurrences */}
                            {analytics.co_occurrences?.length > 0 && (
                                <div className="bg-white border border-stone-200 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Common Tag Pairs
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2">
                                        {analytics.co_occurrences.map(co => (
                                            <div key={co.pair} className="flex items-center justify-between px-3 py-2 bg-stone-50 rounded-lg">
                                                <span className="text-xs text-stone-700 font-medium">{co.pair}</span>
                                                <span className="text-[10px] text-stone-400">{co.count} contacts</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <BarChart3 className="w-10 h-10 text-stone-200 mx-auto mb-3" />
                            <p className="text-sm text-stone-500">No analytics data available</p>
                        </div>
                    )}
                </div>
            )}

            {/* v2.16.39 BUG-049/BUG-010 — Create Tag dialog */}
            <Dialog open={showCreateTag} onOpenChange={setShowCreateTag}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Tag className="w-5 h-5" /> New Tag
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="text-xs font-semibold text-stone-700 mb-1 block">Tag name</label>
                            <input
                                value={newTagName}
                                onChange={e => setNewTagName(e.target.value)}
                                placeholder="e.g. VIP, Attended Webinar Q4"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter' && newTagName.trim()) handleCreateTag(); }}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-stone-700 mb-1 block">Colour</label>
                            <div className="flex flex-wrap gap-1.5">
                                {TAG_COLORS.map(c => (
                                    <button
                                        key={c.value}
                                        type="button"
                                        onClick={() => setNewTagColor(c.value)}
                                        title={c.name}
                                        className={`w-7 h-7 rounded-full border-2 transition-transform ${newTagColor === c.value ? 'border-stone-900 scale-110' : 'border-transparent hover:scale-105'}`}
                                        style={{ backgroundColor: c.value }}
                                    />
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-stone-700 mb-1 block">Category <span className="text-stone-400 font-normal">(optional)</span></label>
                            <input
                                value={newTagCategory}
                                onChange={e => setNewTagCategory(e.target.value)}
                                placeholder="e.g. Events, Segmentation, Stage"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            />
                        </div>
                        <p className="text-[11px] text-stone-500">
                            Created tags appear in the grid immediately with 0 contacts and can be applied from the Contacts / Companies / Deals pages.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateTag(false)} disabled={creatingTag}>Cancel</Button>
                        <Button onClick={handleCreateTag} disabled={creatingTag || !newTagName.trim()}>
                            {creatingTag ? 'Creating…' : 'Create Tag'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteTag} onOpenChange={() => setDeleteTag(null)}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" /> Delete Tag
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-stone-600 py-2">
                        Remove the tag <strong>"{deleteTag}"</strong> from all contacts, companies, and deals? This cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTag(null)}>Cancel</Button>
                        <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
                            {deleting ? 'Deleting...' : 'Delete Tag'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Merge Dialog */}
            <Dialog open={showMergeModal} onOpenChange={setShowMergeModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Merge className="w-5 h-5" /> Merge Tags
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Tags to merge</label>
                            <div className="flex flex-wrap gap-1.5 p-2 bg-stone-50 rounded-lg border border-stone-200">
                                {Array.from(selectedTags).map(t => (
                                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-stone-200 text-stone-700">
                                        <Tag className="w-3 h-3" /> {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Merge into (target tag)</label>
                            <input
                                value={mergeTarget}
                                onChange={e => setMergeTarget(e.target.value)}
                                placeholder="Enter the tag name to keep..."
                                autoFocus
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            />
                            <p className="text-[10px] text-stone-400 mt-1">All selected tags will be replaced with this tag</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowMergeModal(false)}>Cancel</Button>
                        <Button onClick={handleMerge} disabled={!mergeTarget.trim() || merging} className="bg-stone-900 hover:bg-stone-800">
                            {merging ? 'Merging...' : `Merge ${selectedTags.size} Tags`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Dialog */}
            <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Upload className="w-5 h-5" /> Import Tags from CSV
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                            <p className="text-xs text-stone-600 font-medium mb-1">CSV Format</p>
                            <code className="text-[11px] text-stone-500 block">email,tags</code>
                            <code className="text-[11px] text-stone-500 block">john@acme.com,"VIP, Enterprise"</code>
                            <code className="text-[11px] text-stone-500 block">jane@corp.io,"Webinar, Decision Maker"</code>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Upload CSV</label>
                            <input type="file" accept=".csv,.txt" onChange={handleFileUpload}
                                className="w-full text-sm text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200" />
                        </div>
                        {csvText && (
                            <div>
                                <p className="text-xs text-stone-500">{csvText.split('\n').length - 1} rows detected</p>
                            </div>
                        )}
                        {importResult && (
                            <div className={`rounded-lg p-3 ${importResult.matched > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                                <p className="text-sm font-medium text-stone-900">Import Results</p>
                                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                                    <div>Rows processed: <strong>{importResult.processed}</strong></div>
                                    <div>Contacts matched: <strong>{importResult.matched}</strong></div>
                                    <div>Not found: <strong>{importResult.not_found}</strong></div>
                                    <div>Tags applied: <strong>{importResult.tags_added}</strong></div>
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowImportModal(false)}>
                            {importResult ? 'Close' : 'Cancel'}
                        </Button>
                        {!importResult && (
                            <Button onClick={handleImport} disabled={!csvText || importing} className="bg-stone-900 hover:bg-stone-800">
                                {importing ? 'Importing...' : 'Import Tags'}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
